import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle,
  Shield,
  BookOpen,
  Clock,
  Users,
  Sparkles,
  Target,
  Award,
  Zap,
  TrendingUp,
  Heart,
} from 'lucide-react';
import SEO from '../components/SEO';
import { PageHero } from '../components/PageComponents';
import { dienstenDetailData } from '../data/extraMockData';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const serviceTheme = {
  'Interim Management': {
    icon: Clock,
    accent: '#00897B',
    image:
      'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1400&q=85&auto=format&fit=crop',
    imageSecondary:
      'https://images.unsplash.com/photo-1552664730-d307ca884978?w=700&q=85&auto=format&fit=crop',
    tag: 'Direct inzetbaar',
  },
  'Werving & Selectie': {
    icon: Users,
    accent: '#0d1b2a',
    image:
      'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1400&q=85&auto=format&fit=crop',
    imageSecondary:
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=700&q=85&auto=format&fit=crop',
    tag: 'Vaste plaatsing',
  },
  Detavast: {
    icon: Shield,
    accent: '#00897B',
    image:
      'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=1400&q=85&auto=format&fit=crop',
    imageSecondary:
      'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=700&q=85&auto=format&fit=crop',
    tag: 'Flexibel + vast',
  },
  Duurzaamheidsconsultants: {
    icon: BookOpen,
    accent: '#2e7d32',
    image:
      'https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?w=1400&q=85&auto=format&fit=crop',
    imageSecondary:
      'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=700&q=85&auto=format&fit=crop',
    tag: 'CSRD & ESG',
  },
};

const ParallaxImage = ({ src, alt, className = '' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="w-full h-full object-cover scale-110"
        loading="lazy"
      />
    </div>
  );
};

const DienstenPage = () => {
  const diensten = Object.values(dienstenDetailData);

  return (
    <main className="min-h-screen bg-white">
      <SEO
        title="Interim Management Finance & HR – Staffing Force"
        description="Ontdek onze 4 diensten: Interim Management, Werving & Selectie, Detavast en Duurzaamheidsconsultants. Gespecialiseerd in Finance, HR en ESG sinds 2007."
      />

      <PageHero
        label="Onze expertise"
        title="Diensten die werken"
        description="Van interim management tot duurzaamheidsconsultancy – wij bieden de juiste oplossing voor elke vraag. Ontdek wat Staffing Force voor uw organisatie kan betekenen."
        image="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920&q=85&auto=format&fit=crop"
      />

      {/* Service pillars quick grid */}
      <section className="py-20 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-14">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">
                4 kerndisciplines
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 leading-tight">
                Elke uitdaging, een passende aanpak
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {diensten.map((d, i) => {
              const theme = serviceTheme[d.title] || serviceTheme['Interim Management'];
              const Icon = theme.icon;
              return (
                <FadeIn key={i} delay={i * 0.08}>
                  <a
                    href={`#${d.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
                    className="group block relative h-60 rounded-2xl overflow-hidden cursor-pointer"
                  >
                    <img
                      src={theme.image}
                      alt={d.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div
                      className="absolute inset-0 transition-opacity duration-500"
                      style={{
                        background: `linear-gradient(180deg, transparent 0%, ${theme.accent}CC 70%, ${theme.accent}EE 100%)`,
                      }}
                    />
                    <div className="relative h-full flex flex-col justify-end p-5 text-white">
                      <Icon size={22} className="mb-3 opacity-80" />
                      <h3 className="text-lg font-bold leading-tight">{d.title}</h3>
                      <span className="text-xs opacity-80 mt-1 flex items-center gap-1 group-hover:gap-2 transition-all">
                        Ontdek meer <ArrowRight size={12} />
                      </span>
                    </div>
                  </a>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed service sections */}
      {diensten.map((d, i) => {
        const theme = serviceTheme[d.title] || serviceTheme['Interim Management'];
        const Icon = theme.icon;
        const isEven = i % 2 === 0;
        const anchor = d.title.toLowerCase().replace(/[^a-z]/g, '-');
        return (
          <section
            key={i}
            id={anchor}
            className={`py-28 relative overflow-hidden ${isEven ? 'bg-gray-50' : 'bg-white'}`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
              <div
                className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                  !isEven ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Text */}
                <div className={`lg:col-span-6 ${!isEven ? 'lg:col-start-7' : ''}`}>
                  <FadeIn>
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase mb-6"
                      style={{
                        backgroundColor: `${theme.accent}15`,
                        color: theme.accent,
                      }}
                    >
                      <Sparkles size={12} />
                      {theme.tag}
                    </span>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-[1.05]">
                      {d.title}
                    </h2>
                    <p
                      className="text-lg font-medium mb-6"
                      style={{ color: theme.accent }}
                    >
                      {d.subtitle}
                    </p>
                    <p className="text-gray-600 leading-relaxed mb-8 text-base">
                      {d.description}
                    </p>
                    <ul className="space-y-3 mb-10">
                      {d.features.map((f, fi) => (
                        <li
                          key={fi}
                          className="flex items-start gap-3 text-gray-700 text-sm"
                        >
                          <span
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: `${theme.accent}20` }}
                          >
                            <CheckCircle size={12} style={{ color: theme.accent }} />
                          </span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {/* Inline stats */}
                    <div className="grid grid-cols-3 gap-3 mb-10 py-6 border-t border-b border-gray-200">
                      {Object.entries(d.stats).map(([key, val], si) => (
                        <div key={si}>
                          <p
                            className="text-2xl md:text-3xl font-bold leading-none"
                            style={{ color: theme.accent }}
                          >
                            {val}
                          </p>
                          <p className="text-[10px] text-gray-500 mt-1.5 uppercase tracking-wider leading-tight">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-lg font-semibold text-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                        style={{ backgroundColor: theme.accent }}
                      >
                        Meer informatie <ArrowRight size={15} />
                      </Link>
                      <Link
                        to="/vacatures"
                        className="inline-flex items-center gap-2 text-gray-700 border-2 border-gray-200 hover:border-gray-900 hover:text-gray-900 px-7 py-3.5 rounded-lg font-medium text-sm transition-all"
                      >
                        Bekijk vacatures
                      </Link>
                    </div>
                  </FadeIn>
                </div>

                {/* Visual — two-image layered composition */}
                <div className={`lg:col-span-6 ${!isEven ? 'lg:col-start-1' : ''}`}>
                  <FadeIn delay={0.2}>
                    <div className="relative h-[520px] md:h-[580px]">
                      {/* Main image */}
                      <div className="absolute top-0 right-0 w-[80%] h-[70%] rounded-2xl overflow-hidden shadow-2xl">
                        <ParallaxImage
                          src={theme.image}
                          alt={d.title}
                          className="w-full h-full"
                        />
                        <div
                          className="absolute inset-0 mix-blend-multiply opacity-30"
                          style={{ backgroundColor: theme.accent }}
                        />
                      </div>

                      {/* Secondary image overlapping */}
                      <div className="absolute bottom-0 left-0 w-[55%] h-[50%] rounded-2xl overflow-hidden shadow-2xl border-8 border-white z-10">
                        <img
                          src={theme.imageSecondary}
                          alt={`${d.title} – Staffing Force Amsterdam`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      </div>

                      {/* Floating icon card */}
                      <div
                        className="absolute top-12 -left-2 md:-left-6 z-20 rounded-2xl p-5 shadow-2xl text-white"
                        style={{
                          background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent}DD)`,
                        }}
                      >
                        <Icon size={28} />
                        <p className="text-[10px] font-bold uppercase tracking-widest mt-3 opacity-80">
                          0{i + 1} / 04
                        </p>
                      </div>

                      {/* Floating badge */}
                      <div className="absolute bottom-6 right-6 z-20 bg-white rounded-xl px-4 py-3 shadow-xl border border-gray-100 flex items-center gap-2">
                        <Award size={16} style={{ color: theme.accent }} />
                        <span className="text-xs font-semibold text-gray-900">{theme.tag}</span>
                      </div>

                      {/* Decorative blur */}
                      <div
                        className="absolute -top-8 -right-8 w-32 h-32 rounded-full blur-3xl opacity-30 -z-10"
                        style={{ backgroundColor: theme.accent }}
                      />
                    </div>
                  </FadeIn>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Approach / Values band with big image */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=1920&q=85&auto=format&fit=crop"
            alt="Modern kantoor met team in overleg – Staffing Force"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b2a] via-[#0d1b2a]/90 to-[#0d1b2a]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="max-w-2xl">
              <span className="text-[#00bfa5] text-sm font-semibold tracking-widest uppercase">
                Onze waarden
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6 leading-tight">
                Meer dan alleen een bureau
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-10">
                Sinds 2007 zijn we pionier in duurzame detachering. Geen standaard aanpak, maar
                een partnerschap dat verder gaat dan de plaatsing.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Target, title: 'Resultaatgericht', desc: 'Gemiddeld 48-72 uur tot match' },
              { icon: Heart, title: 'Persoonlijk', desc: 'Vaste contactpersoon bij elke opdracht' },
              { icon: Zap, title: 'Snel', desc: '500+ professionals in ons netwerk' },
              { icon: TrendingUp, title: 'Duurzaam', desc: 'B Corp gecertificeerd sinds 2023' },
            ].map((v, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 h-full hover:bg-white/10 hover:border-[#00bfa5]/40 transition-all group">
                  <v.icon size={28} className="text-[#00bfa5] mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-bold text-lg mb-1">{v.title}</h3>
                  <p className="text-white/60 text-sm">{v.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Process / "Zo werken wij" strip */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeIn>
            <div className="text-center mb-16">
              <span className="text-[#00897B] text-sm font-semibold tracking-widest uppercase">
                Onze aanpak
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3">
                Zo werken wij
              </h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              {
                nr: '01',
                title: 'Intake',
                desc: 'Persoonlijk gesprek om behoefte, cultuur en ambities te begrijpen.',
                image:
                  'https://images.unsplash.com/photo-1560439514-4e9645039924?w=700&q=85&auto=format&fit=crop',
              },
              {
                nr: '02',
                title: 'Shortlist',
                desc: 'Binnen 48-72 uur presenteren wij een gekwalificeerde shortlist.',
                image:
                  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=700&q=85&auto=format&fit=crop',
              },
              {
                nr: '03',
                title: 'Match',
                desc: 'Gesprekken, assessment en keuze voor de juiste professional.',
                image:
                  'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=700&q=85&auto=format&fit=crop',
              },
              {
                nr: '04',
                title: 'Begeleiding',
                desc: 'Start met vaste contactpersoon en continue evaluatie.',
                image:
                  'https://images.unsplash.com/photo-1552664730-d307ca884978?w=700&q=85&auto=format&fit=crop',
              },
            ].map((s, i) => (
              <FadeIn key={s.nr} delay={i * 0.08}>
                <div className="group relative rounded-2xl overflow-hidden h-72 cursor-default">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b2a]/95 via-[#0d1b2a]/60 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-6 text-white">
                    <span className="text-[#00bfa5] font-bold text-xs tracking-[0.2em] mb-2">
                      STAP {s.nr}
                    </span>
                    <h3 className="text-2xl font-bold mb-2">{s.title}</h3>
                    <p className="text-white/80 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden bg-[#0d1b2a]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#00897B]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#00bfa5]/10 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <FadeIn>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Klaar om samen te werken?
            </h2>
            <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">
              Binnen 48 uur presenteren wij de juiste kandidaten of bespreken wij de mogelijkheden
              voor uw volgende stap.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link
                to="/contact"
                className="bg-[#00897B] hover:bg-[#00796B] text-white px-8 py-4 rounded-lg font-semibold text-sm transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#00897B]/30 inline-flex items-center gap-2"
              >
                Neem contact op <ArrowRight size={16} />
              </Link>
              <Link
                to="/vacatures"
                className="border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded-lg font-semibold text-sm transition-all hover:-translate-y-0.5"
              >
                Bekijk vacatures
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default DienstenPage;
